/**
 * Ricerca della vista "Tutte" del drawer Messaggi (logica pura, test in node).
 *
 * Allineata alla scheda WhatsApp (useWhatsAppSearch): oltre a nome e ultimo
 * messaggio, le chat WhatsApp si trovano per numero, cercato sull'id della chat
 * (393331234567@c.us) con o senza 39, +39, spazi e trattini. Le chat che la
 * ricerca WhatsApp trova in piu' (non ancora caricate, o col nome del Lead/Cliente)
 * vengono aggiunte in coda.
 */
import { buildPhoneDigitPatterns, normalizePhoneDigits } from './phoneSearch';
import { mergeChatLists, type UnifiedChatItem, type WhatsAppChatLike } from './chatListMerging';

const MIN_PHONE_DIGITS = 3;

/** True se il numero dell'id della chat contiene quanto digitato (qualunque formato). */
export function chatIdMatchesNumber(chatId: string, query: string): boolean {
  const digits = normalizePhoneDigits(query);
  if (digits.length < MIN_PHONE_DIGITS) return false;
  // Solo cifre, spazi, +, trattini, parentesi, punti: altrimenti e' un nome
  if (!/^[\d\s+\-().]+$/.test(query.trim())) return false;
  const idDigits = normalizePhoneDigits(chatId.split('@')[0]);
  if (!idDigits) return false;
  return buildPhoneDigitPatterns(query).some((p) => idDigits.includes(p));
}

/** Una conversazione corrisponde alla ricerca: nome, ultimo messaggio o (WhatsApp) numero. */
export function inboxItemMatches(item: UnifiedChatItem, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  if (item.name.toLowerCase().includes(q) || item.snippet.toLowerCase().includes(q)) return true;
  if (item.source !== 'whatsapp') return false;
  return item.id.toLowerCase().includes(q) || chatIdMatchesNumber(item.id, query);
}

/**
 * Risultati della ricerca: le conversazioni in lista che corrispondono, con in piu'
 * quelle che la ricerca WhatsApp ha trovato (`whatsappHits`). Senza doppioni.
 */
export function searchInboxItems(
  items: UnifiedChatItem[],
  query: string,
  whatsappHits: WhatsAppChatLike[] = [],
): UnifiedChatItem[] {
  if (!query.trim()) return items;
  const hitIds = new Set(whatsappHits.map((c) => c.id));
  const matching = items.filter((i) => inboxItemMatches(i, query) || (i.source === 'whatsapp' && hitIds.has(i.id)));
  const present = new Set(items.filter((i) => i.source === 'whatsapp').map((i) => i.id));
  const extra = whatsappHits.filter((c) => !present.has(c.id));
  return extra.length > 0 ? [...matching, ...mergeChatLists(extra, [], [])] : matching;
}
